import React, { useState, useEffect, useRef } from "react";
import { youtubeFacts } from "../../utils/youtube/youtubeFacts";
import { LanguageCode, pickLang } from "../../utils/translations";
import { COLORS } from "../../utils/constants";

interface FactCarouselProps {
  lang: LanguageCode;
}

const CAROUSEL_LABELS: Partial<
  Record<LanguageCode, { back: string; next: string }>
> = {
  en: { back: "Back", next: "Next Vibe⚡" },
  hi: { back: "पीछे", next: "अगला तथ्य⚡" },
  fr: { back: "Retour", next: "Faits suivants⚡" },
};

const FactCarousel: React.FC<FactCarouselProps> = ({ lang }) => {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState<"next" | "prev">("next");

  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);

  const currentLangFacts = pickLang(youtubeFacts, lang);
  const fact = currentLangFacts[index];
  const runDelay = 8000;
  const next = () => {
    setDirection("next");
    setIndex((prev) => (prev + 1) % currentLangFacts.length);
  };

  const prev = () => {
    setDirection("prev");
    setIndex((prev) => (prev === 0 ? currentLangFacts.length - 1 : prev - 1));
  };

  /* ✅ AUTO-RUN every 8 seconds */
  useEffect(() => {
    const interval = setInterval(() => {
      setDirection("next");
      setIndex((prev) => (prev + 1) % currentLangFacts.length);
    }, runDelay);

    return () => clearInterval(interval);
  }, [lang, currentLangFacts.length]);

  /* ✅ Swipe handlers */
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (touchStartX.current === null || touchEndX.current === null) return;

    const deltaX = touchStartX.current - touchEndX.current;

    if (Math.abs(deltaX) > 50) {
      if (deltaX > 0) next(); // swipe left
      else prev(); // swipe right
    }

    touchStartX.current = null;
    touchEndX.current = null;
  };

  return (
    <div style={{ margin: "20px 0", textAlign: "center" }}>
      {/* Inject animations */}
      <style>{`
        @keyframes slideFromRight {
          from { transform: translateX(40px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes slideFromLeft {
          from { transform: translateX(-40px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>

      {/* Content Card */}
      <div
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          backgroundColor: "#fff",
          padding: "20px 24px",
          borderRadius: "16px",
          border: "1px solid #f0f0f0",
          maxWidth: "720px",
          margin: "0 auto 20px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.03)",
          touchAction: "pan-y",
        }}
      >
        {/* Animated content */}
        <div
          key={index}
          style={{
            animation:
              direction === "next"
                ? "slideFromRight 0.35s ease-out"
                : "slideFromLeft 0.35s ease-out",
          }}
        >
          <h2
            style={{
              fontSize: "1.5rem",
              marginBottom: "0.5rem",
              color: COLORS.brandPurple,
              fontWeight: 800,
              marginTop: 0,
            }}
          >
            {fact.title}
          </h2>

          <p
            style={{
              maxWidth: "550px",
              margin: "0 auto 1.2rem",
              color: "#4A5568",
              lineHeight: "1.5",
              fontSize: "0.95rem",
            }}
          >
            {fact.insight}
          </p>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: "8px",
            }}
          >
            {fact.videos.map((v, i) => (
              <div
                key={i}
                style={{
                  background: "#f8fafc",
                  padding: "6px 12px",
                  borderRadius: "8px",
                  fontSize: "0.8rem",
                  color: "#1A202C",
                  border: "1px solid #edf2f7",
                }}
              >
                <span style={{ color: COLORS.brandTeal }}>{v.title}</span>
                <span style={{ opacity: 0.7 }}> ({v.published})</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Navigation Controls */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "15px",
        }}
      >
        <button
          onClick={prev}
          style={{
            background: "transparent",
            color: COLORS.brandPurple,
            border: `1px solid ${COLORS.brandPurple}`,
            padding: "8px 20px",
            borderRadius: "50px",
            fontSize: "0.75rem",
            fontWeight: 700,
            textTransform: "uppercase",
            cursor: "pointer",
          }}
        >
          {pickLang(CAROUSEL_LABELS, lang).back}
        </button>

        <div style={{ display: "flex", gap: "6px" }}>
          {currentLangFacts.map((_, i) => (
            <div
              key={i}
              style={{
                width: i === index ? "18px" : "6px",
                height: "6px",
                borderRadius: "4px",
                background: i === index ? COLORS.brandTeal : "#e2e8f0",
                transition: "all 0.3s ease",
              }}
            />
          ))}
        </div>

        <button
          onClick={next}
          style={{
            background: COLORS.brandTeal,
            color: "white",
            border: "none",
            padding: "8px 20px",
            borderRadius: "50px",
            fontSize: "0.75rem",
            fontWeight: 700,
            textTransform: "uppercase",
            cursor: "pointer",
            boxShadow: "0 2px 8px rgba(1, 126, 132, 0.2)",
          }}
        >
          {pickLang(CAROUSEL_LABELS, lang).next}
        </button>
      </div>
    </div>
  );
};

export default FactCarousel;
